import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, FileText, Building2, CheckCircle } from 'lucide-react';
import type { PartnerProjectView } from '@/types';

interface PartnerProjectCardProps {
  project: PartnerProjectView;
}

// Status colors matching portal status labels synced via portalSync
const statusColors: Record<string, string> = {
  '1. Contract Needed': 'bg-red-100 text-red-800',
  '1.1 Contract Sent': 'bg-red-50 text-red-700',
  '1.2 Contract Signed': 'bg-orange-100 text-orange-800',
  '2. $$ Owed': 'bg-orange-100 text-orange-800',
  '3. Planning': 'bg-yellow-100 text-yellow-800',
  '3.1 Ready to Schedule': 'bg-yellow-100 text-yellow-800',
  '4. Scheduled': 'bg-lime-100 text-lime-800',
  '5. Job Started': 'bg-green-100 text-green-800',
  '6. Punch-List': 'bg-green-100 text-green-800',
  '7. Final Walk/COS': 'bg-emerald-100 text-emerald-800',
  '8. Reconciliation': 'bg-teal-100 text-teal-800',
  '9. Waiting For Final': 'bg-teal-100 text-teal-800',
  '10. Completed': 'bg-green-100 text-green-800',
  'Delayed': 'bg-gray-100 text-gray-800',
  'Job Not Sold': 'bg-gray-100 text-gray-600',
  'Warranty': 'bg-purple-100 text-purple-800',
  // Legacy statuses
  'New': 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-yellow-100 text-yellow-800',
  'Working on it': 'bg-yellow-100 text-yellow-800',
  'Completed': 'bg-green-100 text-green-800',
  'Done': 'bg-green-100 text-green-800',
  'Closed': 'bg-green-100 text-green-800',
  'On Hold': 'bg-orange-100 text-orange-800',
  'Stuck': 'bg-red-100 text-red-800',
  'Cancelled': 'bg-gray-100 text-gray-800',
};

function getStatusColor(status: string): string {
  return statusColors[status] || 'bg-gray-100 text-gray-800';
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getBusinessLabel(business: string): string {
  return business === 'azdisaster' ? 'ACR' : 'Blacksmith';
}

export function PartnerProjectCard({ project }: PartnerProjectCardProps) {
  return (
    <Link
      to={`/partner/projects/${project.id}`}
      className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200"
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {project.name}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
              {project.projectType && (
                <span className="text-xs text-gray-500">
                  {project.projectType}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="text-gray-400 flex-shrink-0" size={20} />
        </div>

        {/* Details */}
        <div className="space-y-2">
          {/* Business */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Building2 size={16} className="text-gray-400" />
            <span>{getBusinessLabel(project.business)}</span>
          </div>

          {/* Close Date */}
          {project.closeDate && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar size={16} className="text-gray-400" />
              <span>Close Date: {formatDate(project.closeDate)}</span>
            </div>
          )}

          {/* Contract Signed */}
          {project.contractSignedDate && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FileText size={16} className="text-gray-400" />
              <span>Contract Signed: {formatDate(project.contractSignedDate)}</span>
            </div>
          )}
        </div>

        {/* Commission Display - Only if Paid */}
        {project.partnerCommissionPaid && project.partnerCommissionPaidAmount ? (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Commission Paid</span>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-green-600">
                  ${project.partnerCommissionPaidAmount.toLocaleString()}
                </span>
                <CheckCircle size={20} className="text-green-600" />
              </div>
            </div>
            {project.partnerCommissionPaidDate && (
              <p className="text-xs text-gray-500 mt-1 text-right">
                Paid on {formatDate(
                  typeof project.partnerCommissionPaidDate === 'string'
                    ? project.partnerCommissionPaidDate
                    : project.partnerCommissionPaidDate.toISOString()
                )}
              </p>
            )}
          </div>
        ) : (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-500 italic">
              Commission pending processing
            </p>
          </div>
        )}
      </div>
    </Link>
  );
}
